import { ref, get, set, update } from 'firebase/database';
import { db } from '../firebase';

const TOKEN_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7日

const generateToken = () => {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * 招待トークンの発行・検証を行うフック
 */
export const useInvitations = () => {
  /**
   * 招待リンクを生成してクリップボードにコピー
   * @returns {{ success: boolean, token?: string, error?: string }}
   */
  const createInviteLink = async ({ eventId, ownerId }) => {
    try {
      const token = generateToken();
      const inviteRef = ref(db, `invitations/${token}`);
      await set(inviteRef, {
        event_id: eventId,
        owner_id: ownerId,
        created_at: Date.now(),
        expires_at: Date.now() + TOKEN_EXPIRY_MS,
        status: 'pending',
      });

      const url = `${window.location.origin}/invite/${token}`;
      await navigator.clipboard.writeText(url);
      return { success: true, token, url };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  /**
   * トークンを検証してイベント情報を返す
   * @returns {{ valid: boolean, invitation?: object, error?: string }}
   */
  const validateToken = async (token) => {
    try {
      const inviteRef = ref(db, `invitations/${token}`);
      const snapshot = await get(inviteRef);

      if (!snapshot.exists()) {
        return { valid: false, error: '招待リンクが無効です。' };
      }

      const invitation = snapshot.val();

      if (invitation.status === 'used') {
        return { valid: false, error: 'この招待リンクはすでに使用されています。' };
      }

      if (Date.now() > invitation.expires_at) {
        return { valid: false, error: '招待リンクの有効期限が切れています（7日間有効）。' };
      }

      // event_name のみ取得（未ログインでも .read: true なフィールド）
      const eventNameRef = ref(db, `events/${invitation.event_id}/event_name`);
      const eventNameSnapshot = await get(eventNameRef);
      const eventName = eventNameSnapshot.val();

      return {
        valid: true,
        invitation,
        event: { id: invitation.event_id, event_name: eventName },
      };
    } catch (err) {
      return { valid: false, error: '招待リンクの確認中にエラーが発生しました。' };
    }
  };

  /**
   * トークンを使用済みにしてstaff_membersに追加
   */
  const consumeToken = async (token, staffUid, invitedBy) => {
    try {
      const inviteRef = ref(db, `invitations/${token}`);
      const snapshot = await get(inviteRef);
      if (!snapshot.exists()) throw new Error('トークンが見つかりません');

      const invitation = snapshot.val();

      // display_nameをusers/から取得してstaff_membersにキャッシュ
      const userSnap = await get(ref(db, `users/${staffUid}/display_name`));
      const displayName = userSnap.val() || '';

      // staff_membersに追加
      const memberRef = ref(db, `events/${invitation.event_id}/staff_members/${staffUid}`);
      await set(memberRef, {
        joined_at: Date.now(),
        memo: '',
        invited_by: invitedBy || invitation.owner_id,
        display_name: displayName,
      });

      // トークンを使用済みに
      await update(inviteRef, { status: 'used', used_by: staffUid, used_at: Date.now() });

      return { success: true, eventId: invitation.event_id };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return { createInviteLink, validateToken, consumeToken };
};
